import { AlertTriangle, X } from 'lucide-react';
import { useEffect, useState } from 'react';

type HealthLevel = 'info' | 'warning' | 'high' | 'critical';

interface HealthFlag {
	code: string;
	level: HealthLevel;
	title: string;
	message: string;
	action?: { label: string; hint?: string };
}

interface HealthSnapshot {
	flags: HealthFlag[];
	overall: 'healthy' | 'degraded' | 'critical';
	checkedAt: number;
}

const POLL_MS = 60_000;
const DISMISSED_KEY = 'links-health-dismissed';

function readDismissed(): Record<string, number> {
	try {
		return JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? '{}');
	} catch {
		return {};
	}
}

function writeDismissed(map: Record<string, number>): void {
	localStorage.setItem(DISMISSED_KEY, JSON.stringify(map));
}

export function HealthBanner() {
	const [flags, setFlags] = useState<HealthFlag[]>([]);
	const [dismissed, setDismissed] = useState<Record<string, number>>(readDismissed);

	useEffect(() => {
		let cancelled = false;
		const fetchHealth = async () => {
			try {
				const res = await fetch('/api/status/health');
				if (!res.ok) return;
				const snap: HealthSnapshot = await res.json();
				if (!cancelled) setFlags(snap.flags);
			} catch {
				// silent — health is best-effort
			}
		};
		void fetchHealth();
		const interval = setInterval(fetchHealth, POLL_MS);
		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, []);

	const visible = flags.filter(
		(f) => (f.level === 'high' || f.level === 'critical') && !dismissed[f.code]
	);

	if (visible.length === 0) return null;

	const dismiss = (code: string) => {
		const next = { ...dismissed, [code]: Date.now() };
		setDismissed(next);
		writeDismissed(next);
	};

	return (
		<div className="border-b border-border bg-background shrink-0">
			{visible.map((f) => {
				const tone =
					f.level === 'critical'
						? 'bg-red-950/40 border-red-700 text-red-200'
						: 'bg-yellow-950/40 border-yellow-700 text-yellow-200';
				return (
					<div
						key={f.code}
						className={`flex items-start gap-2 border-l-4 px-3 py-2 text-sm ${tone}`}
					>
						<AlertTriangle className="size-4 shrink-0 mt-0.5" />
						<div className="flex-1 min-w-0">
							<div className="font-mono font-semibold">{f.title}</div>
							<div className="text-xs opacity-90">{f.message}</div>
							{f.action?.hint && (
								<div className="text-xs opacity-70 mt-0.5">→ {f.action.hint}</div>
							)}
						</div>
						<button
							type="button"
							onClick={() => dismiss(f.code)}
							className="opacity-60 hover:opacity-100"
							title="Dismiss until next change"
						>
							<X className="size-4" />
						</button>
					</div>
				);
			})}
		</div>
	);
}
